'use client';
import { assertNever } from '@/globals/helpers/assert.helpers';
import { useLinksToSelfClicked } from '@/globals/routing/DOPRouter';
import { usePathname } from 'next/navigation';
import React, {
	createContext,
	useContext,
	ReactNode,
	useEffect,
	useReducer,
	useRef,
} from 'react';

type OpenedMenu = {
	mainMenu: boolean;
	submenu: string | undefined;
	menuType: ResponsiveMenuType | undefined;
};
export type ResponsiveMenuType = 'hamburger' | 'navBar';
type MenuAction =
	| {
			type: 'closeSubmenu';
			submenuID: string;
			menuType: ResponsiveMenuType;
	  }
	| {
			type: 'openSubmenu';
			submenuID: string;
			menuType: ResponsiveMenuType;
	  }
	| { type: 'openMainMenu' }
	| { type: 'closeMainMenu' }
	| { type: 'menuType'; menuType: ResponsiveMenuType };

const MainMenuContext = createContext<
	| [openedMenu: OpenedMenu, dispatchOpenedMenu: React.Dispatch<MenuAction>]
	| undefined
>(undefined);

export const useMainMenuOpened = () => {
	const context = useContext(MainMenuContext);
	if (context == null) {
		throw new Error('useMainMenuOpened must be used within a MainMenuProvider');
	}
	return context;
};

const openMainMenuFromNavBar = ({
	mainMenuOpened,
	subMenuOpened,
	menuType,
}: {
	mainMenuOpened: boolean;
	subMenuOpened: boolean;
	menuType: ResponsiveMenuType;
}) => {
	if (menuType === 'hamburger') {
		return mainMenuOpened;
	}

	return subMenuOpened;
};

const reducer = (openedMainMenu: OpenedMenu, action: MenuAction) => {
	switch (action.type) {
		case 'openSubmenu': {
			return {
				submenu: action.submenuID,
				mainMenu: openMainMenuFromNavBar({
					mainMenuOpened: openedMainMenu.mainMenu,
					subMenuOpened: true,
					menuType: action.menuType,
				}),
				menuType: openedMainMenu.menuType,
			};
		}
		case 'closeSubmenu': {
			return {
				submenu: undefined,
				mainMenu: openMainMenuFromNavBar({
					mainMenuOpened: openedMainMenu.mainMenu,
					subMenuOpened: false,
					menuType: action.menuType,
				}),
				menuType: openedMainMenu.menuType,
			};
		}
		case 'openMainMenu': {
			return {
				submenu: openedMainMenu.submenu,
				mainMenu: true,
				menuType: openedMainMenu.menuType,
			};
		}
		case 'closeMainMenu': {
			return {
				submenu: undefined,
				mainMenu: false,
				menuType: openedMainMenu.menuType,
			};
		}
		case 'menuType': {
			return {
				submenu: openedMainMenu.submenu,
				mainMenu: openedMainMenu.mainMenu,
				menuType: action.menuType,
			};
		}
		default:
			return assertNever(action);
	}
};

export const MainMenuProvider = ({ children }: { children: ReactNode }) => {
	const [openedMainMenu, dispatchOpenedMainMenu] = useReducer(reducer, {
		mainMenu: false,
		submenu: undefined,
		menuType: undefined,
	});
	const pathname = usePathname();
	const pathnameRef = useRef<string | undefined>(undefined);
	const linksToSelfClicked = useLinksToSelfClicked();
	const linksToSelfClickedRef = useRef<number | undefined>(undefined);

	// Close on new page load
	useEffect(() => {
		if (pathnameRef.current === pathname) return;
		pathnameRef.current = pathname;
		dispatchOpenedMainMenu({ type: 'closeMainMenu' });
	}, [pathname]);

	// Close on link to self
	useEffect(() => {
		if (linksToSelfClickedRef.current === linksToSelfClicked) return;
		linksToSelfClickedRef.current = linksToSelfClicked;
		dispatchOpenedMainMenu({ type: 'closeMainMenu' });
	}, [linksToSelfClicked]);

	return (
		<MainMenuContext.Provider value={[openedMainMenu, dispatchOpenedMainMenu]}>
			{children}
		</MainMenuContext.Provider>
	);
};
